/**
 * RP Markup — Fase 1 (AST sync).
 * Extensible: RP Tags MAYÚSCULAS con `:` (hoy JUTSU; mañana ITEM…).
 * BBCode y texto plano quedan en nodos `text` (híbrido con parseBBCode al render).
 */

/** Tags RP con params `NAME:…` conocidos (+ registry para más adelante). */
export const RP_TAG_HANDLERS = Object.freeze({
  JUTSU: { paramStyle: 'id' },
  ATAQUE: { paramStyle: 'attack' },
})

/** Fuentes públicas de [ATAQUE:fuente@objetivo] (UTF-8). */
export const ATAQUE_BODY_SOURCES = Object.freeze(['puños', 'patada'])
export const ATAQUE_HAND_SOURCES = Object.freeze(['principal', 'secundaria'])

/**
 * @typedef {{ type: 'text', value: string }} TextNode
 * @typedef {{ type: 'rp_tag', name: string, params: Record<string, string|number>, children: Array<TextNode>, selfClosing: boolean }} RpTagNode
 * @typedef {{ type: 'document', children: Array<TextNode|RpTagNode> }} DocumentNode
 */

/**
 * Normaliza y valida la fuente de ATAQUE.
 * @param {string} rawSource
 * @returns {{ ok: true, source: string, itemId: number|null } | { ok: false, error: string }}
 */
export function parseAtaqueSource(rawSource) {
  const part = String(rawSource || '')
    .normalize('NFC')
    .trim()
    .toLowerCase()
  if (!part) {
    return { ok: false, error: 'Fuente de ataque vacía' }
  }

  const objetoMatch = /^objeto:(\d+)$/i.exec(part)
  if (objetoMatch) {
    const itemId = Number(objetoMatch[1])
    if (!Number.isInteger(itemId) || itemId <= 0) {
      return { ok: false, error: `Objeto inválido en ATAQUE:${rawSource}` }
    }
    return { ok: true, source: `objeto:${itemId}`, itemId }
  }

  if (ATAQUE_BODY_SOURCES.includes(part) || ATAQUE_HAND_SOURCES.includes(part)) {
    return { ok: true, source: part, itemId: null }
  }

  return {
    ok: false,
    error: `Fuente inválida en ATAQUE:${rawSource} (usa puños, patada, principal, secundaria u objeto:id)`,
  }
}

/**
 * Parsea sufijo de objetivo `@12` / `@n:12`.
 * @param {string} targetPart
 * @param {string} name
 * @param {string} rawParam
 */
function parseTargetSuffix(targetPart, name, rawParam) {
  /** @type {Record<string, string|number>} */
  const params = {}
  if (!targetPart) return { ok: true, params }
  const combatantMatch = /^n:(\d+)$/i.exec(targetPart)
  if (combatantMatch) {
    const tid = Number(combatantMatch[1])
    if (!Number.isInteger(tid) || tid <= 0) {
      return { ok: false, error: `Objetivo inválido en [${name}:${rawParam}]` }
    }
    params.targetKind = 'combatant'
    params.targetRefId = tid
    return { ok: true, params }
  }
  const tid = Number(targetPart)
  if (!Number.isInteger(tid) || tid <= 0) {
    return { ok: false, error: `Objetivo inválido en [${name}:${rawParam}]` }
  }
  params.targetCharacterId = tid
  params.targetKind = 'character'
  params.targetRefId = tid
  return { ok: true, params }
}

/**
 * Parsea param de RP Tag:
 * - estilo `id` (JUTSU): `21`, `21@2`, `21@n:12`
 * - estilo `attack` (ATAQUE): `puños@12`, `principal@n:5`, `objeto:201@12`
 * @param {string} name
 * @param {string} rawParam
 */
export function parseRpTagParams(name, rawParam) {
  const handler = RP_TAG_HANDLERS[name]
  if (handler?.paramStyle === 'attack') {
    const raw = String(rawParam || '').trim()
    const atIdx = raw.indexOf('@')
    const sourcePart = (atIdx === -1 ? raw : raw.slice(0, atIdx)).trim()
    const targetPart = atIdx === -1 ? '' : raw.slice(atIdx + 1).trim()

    const sourceParsed = parseAtaqueSource(sourcePart)
    if (!sourceParsed.ok) {
      return { ok: false, error: sourceParsed.error }
    }

    const targetParsed = parseTargetSuffix(targetPart, name, rawParam)
    if (!targetParsed.ok) return targetParsed

    /** @type {Record<string, string|number>} */
    const params = {
      source: sourceParsed.source,
      ...targetParsed.params,
    }
    if (sourceParsed.itemId != null) params.itemId = sourceParsed.itemId
    return { ok: true, params }
  }

  if (!handler || handler.paramStyle === 'id') {
    const raw = String(rawParam || '').trim()
    const atIdx = raw.indexOf('@')
    const idPart = (atIdx === -1 ? raw : raw.slice(0, atIdx)).trim()
    const targetPart = atIdx === -1 ? '' : raw.slice(atIdx + 1).trim()

    let idStr = idPart
    const eqIdx = idPart.indexOf('=')
    if (eqIdx !== -1) {
      const key = idPart.slice(0, eqIdx).trim().toLowerCase()
      if (key === 'id') idStr = idPart.slice(eqIdx + 1).trim()
    }

    const n = Number(idStr)
    if (!Number.isInteger(n) || n <= 0) {
      return { ok: false, error: `ID inválido en [${name}:${rawParam}]` }
    }
    /** @type {Record<string, string|number>} */
    const params = { id: n }
    const targetParsed = parseTargetSuffix(targetPart, name, rawParam)
    if (!targetParsed.ok) return targetParsed
    Object.assign(params, targetParsed.params)
    return { ok: true, params }
  }
  return { ok: true, params: { raw: rawParam } }
}

/**
 * Índice del siguiente `[NAME:` RP Tag registrado, o -1.
 * @param {string} input
 * @param {number} from
 */
function findNextRpTagOpen(input, from) {
  let idx = from
  while (idx < input.length) {
    const bracket = input.indexOf('[', idx)
    if (bracket === -1) return -1
    const close = input.indexOf(']', bracket)
    if (close === -1) return -1
    const inside = input.slice(bracket + 1, close)
    if (inside.startsWith('/')) {
      idx = close + 1
      continue
    }
    const colonIdx = inside.indexOf(':')
    const tagName = colonIdx === -1 ? inside : inside.slice(0, colonIdx)
    if (/^[A-Z][A-Z0-9_]*$/.test(tagName) && Object.prototype.hasOwnProperty.call(RP_TAG_HANDLERS, tagName)) {
      return bracket
    }
    idx = close + 1
  }
  return -1
}

/**
 * Fase 1 — produce AST tipado.
 * @param {string} raw
 * @returns {{ ast: DocumentNode, errors: Array<{ code: string, message: string }> }}
 */
export function parseMarkup(raw) {
  const errors = []
  const input = raw == null ? '' : String(raw)
  /** @type {Array<TextNode|RpTagNode>} */
  const children = []
  let i = 0

  const pushText = (value) => {
    if (!value) return
    const last = children[children.length - 1]
    if (last && last.type === 'text') {
      last.value += value
      return
    }
    children.push({ type: 'text', value })
  }

  while (i < input.length) {
    const openIdx = input.indexOf('[', i)
    if (openIdx === -1) {
      pushText(input.slice(i))
      break
    }

    if (openIdx > i) {
      pushText(input.slice(i, openIdx))
    }

    const closeBracket = input.indexOf(']', openIdx)
    if (closeBracket === -1) {
      pushText(input.slice(openIdx))
      break
    }

    const inside = input.slice(openIdx + 1, closeBracket)
    const colonIdx = inside.indexOf(':')
    const tagName = colonIdx === -1 ? inside : inside.slice(0, colonIdx)
    const rawParam = colonIdx === -1 ? '' : inside.slice(colonIdx + 1)

    const isRpTag =
      /^[A-Z][A-Z0-9_]*$/.test(tagName) && Object.prototype.hasOwnProperty.call(RP_TAG_HANDLERS, tagName)

    if (!isRpTag) {
      // BBCode / desconocido: un solo nodo de texto hasta el próximo RP Tag
      // (si partimos por cada `[…]`, parseBBCode pierde apertura/cierre).
      const nextRp = findNextRpTagOpen(input, openIdx)
      const end = nextRp === -1 ? input.length : nextRp
      pushText(input.slice(openIdx, end))
      i = end
      continue
    }

    const parsed = parseRpTagParams(tagName, rawParam)
    if (!parsed.ok) {
      errors.push({ code: 'INVALID_RP_TAG', message: parsed.error })
      pushText(input.slice(openIdx, closeBracket + 1))
      i = closeBracket + 1
      continue
    }

    const afterOpen = closeBracket + 1
    const closeTag = `[/${tagName}]`
    const closeIdx = input.indexOf(closeTag, afterOpen)
    // Si aparece otro RP Tag abierto antes del cierre, este tag es autocerrado.
    const nextRpOpen = findNextRpTagOpen(input, afterOpen)

    const hasMatchingClose =
      closeIdx !== -1 && (nextRpOpen === -1 || closeIdx < nextRpOpen)

    if (!hasMatchingClose) {
      // Forma cerrada: [JUTSU:20]
      children.push({
        type: 'rp_tag',
        name: tagName,
        params: parsed.params,
        children: [],
        selfClosing: true,
      })
      i = afterOpen
      continue
    }

    const inner = input.slice(afterOpen, closeIdx)
    children.push({
      type: 'rp_tag',
      name: tagName,
      params: parsed.params,
      children: inner ? [{ type: 'text', value: inner }] : [],
      selfClosing: false,
    })
    i = closeIdx + closeTag.length
  }

  return { ast: { type: 'document', children }, errors }
}

/**
 * Recorre el AST y lista refs RP Tag (sin instanceId aún).
 * @param {DocumentNode} ast
 */
export function collectRpTagRefs(ast) {
  const refs = []
  const kids = ast?.children || []
  kids.forEach((node, nodeIndex) => {
    if (node.type !== 'rp_tag') return
    const idRaw = node.params?.id
    const id =
      idRaw != null && Number.isInteger(Number(idRaw)) && Number(idRaw) > 0
        ? Number(idRaw)
        : null
    const attackSource =
      node.params?.source != null ? String(node.params.source) : null
    const itemIdRaw = node.params?.itemId
    const itemId =
      itemIdRaw != null && Number.isInteger(Number(itemIdRaw)) && Number(itemIdRaw) > 0
        ? Number(itemIdRaw)
        : null
    const kindRaw = node.params?.targetKind
    const targetKind =
      kindRaw === 'combatant' || kindRaw === 'character' ? String(kindRaw) : null
    const refRaw = node.params?.targetRefId
    let targetRefId =
      refRaw != null && Number.isInteger(Number(refRaw)) && Number(refRaw) > 0
        ? Number(refRaw)
        : null
    const targetRaw = node.params?.targetCharacterId
    const targetCharacterId =
      targetRaw != null && Number.isInteger(Number(targetRaw)) && Number(targetRaw) > 0
        ? Number(targetRaw)
        : null
    if (!targetRefId && targetCharacterId) targetRefId = targetCharacterId
    const resolvedKind = targetKind || (targetCharacterId ? 'character' : null)
    const innerText =
      !node.selfClosing && node.children?.length
        ? node.children.map((c) => (c.type === 'text' ? c.value : '')).join('')
        : null
    refs.push({
      type: node.name,
      id,
      name: node.name,
      attackSource,
      itemId,
      targetCharacterId: resolvedKind === 'character' ? targetCharacterId || targetRefId : null,
      targetKind: resolvedKind,
      targetRefId: resolvedKind ? targetRefId || targetCharacterId : null,
      innerText: innerText === '' ? null : innerText,
      selfClosing: Boolean(node.selfClosing),
      nodeIndex,
    })
  })
  return refs
}

/**
 * Inserta markup canónico.
 * @param {number} techniqueId
 * @param {{ customText?: string|null, targetCharacterId?: number|null, targetKind?: string|null, targetRefId?: number|null }} [opts]
 */
export function buildJutsuMarkup(techniqueId, opts = {}) {
  const id = Number(techniqueId)
  if (!Number.isInteger(id) || id <= 0) throw new Error('techniqueId inválido')

  let targetSuffix = ''
  const kind = opts.targetKind === 'combatant' ? 'combatant' : opts.targetKind === 'character' ? 'character' : null
  const refId = opts.targetRefId != null ? Number(opts.targetRefId) : null
  const charId = opts.targetCharacterId != null ? Number(opts.targetCharacterId) : null

  if (kind === 'combatant' && Number.isInteger(refId) && refId > 0) {
    targetSuffix = `@n:${refId}`
  } else if (Number.isInteger(charId) && charId > 0) {
    targetSuffix = `@${charId}`
  } else if (kind === 'character' && Number.isInteger(refId) && refId > 0) {
    targetSuffix = `@${refId}`
  }

  const open = `[JUTSU:${id}${targetSuffix}]`
  const custom = opts.customText != null ? String(opts.customText) : null
  if (custom != null && custom !== '') {
    return `${open}${custom}[/JUTSU]`
  }
  return open
}

function buildTargetSuffix(opts = {}) {
  const kind = opts.targetKind === 'combatant' ? 'combatant' : opts.targetKind === 'character' ? 'character' : null
  const refId = opts.targetRefId != null ? Number(opts.targetRefId) : null
  const charId = opts.targetCharacterId != null ? Number(opts.targetCharacterId) : null

  if (kind === 'combatant' && Number.isInteger(refId) && refId > 0) {
    return `@n:${refId}`
  }
  if (Number.isInteger(charId) && charId > 0) {
    return `@${charId}`
  }
  if (kind === 'character' && Number.isInteger(refId) && refId > 0) {
    return `@${refId}`
  }
  return ''
}

/**
 * Inserta markup canónico de ataque blanco.
 * @param {string} source - puños|patada|principal|secundaria|objeto:N
 * @param {{ customText?: string|null, targetCharacterId?: number|null, targetKind?: string|null, targetRefId?: number|null }} [opts]
 */
export function buildAtaqueMarkup(source, opts = {}) {
  const parsed = parseAtaqueSource(source)
  if (!parsed.ok) throw new Error(parsed.error)

  const targetSuffix = buildTargetSuffix(opts)
  const open = `[ATAQUE:${parsed.source}${targetSuffix}]`
  const custom = opts.customText != null ? String(opts.customText) : null
  if (custom != null && custom !== '') {
    return `${open}${custom}[/ATAQUE]`
  }
  return open
}
